/* ===================================================================
   Native Possum — np-card.js
   What a product card does once it is on the page, wherever it was drawn
   (snippets/np-product-card.liquid, by Shopify or by the NP Browse engine):
     - the phone swipe carousel (≤767px) on every card except the hero and
       a card already showing the photo of a ticked colour;
     - a shimmer in the photo frame until the photo has loaded;
     - wishlist hearts painted pressed/unpressed. The wishlist app embed
       owns saving and binds clicks by delegation; it only paints the
       hearts present when it paints, so cards added later are painted here.

   Runs on load and again on 'np:browse:updated' (np-browse.js fires it
   after swapping in new results). Every step marks what it has done, so
   running twice over the same cards is harmless.

   Colour dots are drawn in Liquid (snippets/np-colour-dots), not here.
   =================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------- carousel */
  /* JS-driven transform carousel, no native scroll. A horizontal drag of
     30px moves a slide; anything else is a tap and the card link works. */
  var PHONE = window.matchMedia('(max-width: 767px)');

  function carousel(card) {
    if (card.hasAttribute('data-np-carousel')) return;
    if (card.classList.contains('np-hero-product')) return;
    if (card.classList.contains('np-card--colour-matched')) return;
    var data = card.querySelector('.np-carousel-data');
    var wrapper = card.querySelector('.np-product-image-wrapper');
    if (!data || !wrapper) return;

    var images;
    try { images = JSON.parse(data.textContent); } catch (e) { return; }
    if (!images || images.length < 2) return;
    card.setAttribute('data-np-carousel', '');

    var count = images.length;
    var current = 0;

    var box = document.createElement('div');
    box.className = 'np-card-carousel';
    box.setAttribute('role', 'region');
    box.setAttribute('aria-label', 'Product images, swipe to browse');

    var track = document.createElement('div');
    track.className = 'np-carousel-track';
    images.forEach(function (entry, i) {
      var slide = document.createElement('div');
      slide.className = 'np-carousel-slide';
      var img = document.createElement('img');
      img.src = entry.src;
      img.alt = entry.alt || '';
      img.width = 600;
      img.height = 900;
      img.loading = i === 0 ? 'eager' : 'lazy';
      slide.appendChild(img);
      track.appendChild(slide);
    });
    box.appendChild(track);
    wrapper.classList.add('np-has-carousel');
    wrapper.appendChild(box);

    var width = box.offsetWidth;
    track.style.width = (width * count) + 'px';
    Array.prototype.forEach.call(track.children, function (s) { s.style.width = width + 'px'; });

    var bar = document.createElement('div');
    bar.className = 'np-carousel-bar';
    bar.setAttribute('aria-hidden', 'true');
    var fill = document.createElement('div');
    fill.className = 'np-carousel-bar-fill';
    fill.style.width = (100 / count) + '%';
    bar.appendChild(fill);
    card.classList.add('np-has-carousel-bar');
    card.appendChild(bar);

    function placeBar() { bar.style.top = wrapper.offsetHeight + 'px'; }
    placeBar();
    var first = wrapper.querySelector('img');
    if (first) first.addEventListener('load', placeBar);

    function go(n, animate) {
      current = Math.max(0, Math.min(n, count - 1));
      track.classList.toggle('np-animating', !!animate);
      fill.classList.toggle('np-animating', !!animate);
      track.style.transform = 'translateX(' + -(current * width) + 'px)';
      fill.style.transform = 'translateX(' + (current * 100) + '%)';
    }
    track.addEventListener('transitionend', function () { track.classList.remove('np-animating'); });
    fill.addEventListener('transitionend', function () { fill.classList.remove('np-animating'); });

    var startX = 0, startY = 0, dx = 0, tracking = false, decided = false, sideways = false;

    box.addEventListener('touchstart', function (e) {
      if (e.target.closest('.np-wishlist-btn')) { tracking = false; return; }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      dx = 0; tracking = true; decided = false; sideways = false;
      track.classList.remove('np-animating');
    }, { passive: true });

    box.addEventListener('touchmove', function (e) {
      if (!tracking) return;
      var mx = e.touches[0].clientX - startX;
      var my = e.touches[0].clientY - startY;
      if (!decided) {
        if (Math.abs(mx) > 8 || Math.abs(my) > 8) { decided = true; sideways = Math.abs(mx) > Math.abs(my); }
        if (!decided) return;
      }
      if (!sideways) { tracking = false; return; }
      e.preventDefault();
      dx = mx;
      track.style.transform = 'translateX(' + (-(current * width) + dx) + 'px)';
    }, { passive: false });

    box.addEventListener('touchend', function () {
      if (!sideways) { tracking = false; return; }
      tracking = false;
      if (dx < -30 && current < count - 1) go(current + 1, true);
      else if (dx > 30 && current > 0) go(current - 1, true);
      else go(current, true);
    }, { passive: true });

    /* A swipe must not also follow the card's link. */
    box.addEventListener('click', function (e) {
      if (e.target.closest('.np-wishlist-btn')) return;
      if (sideways) { e.preventDefault(); e.stopPropagation(); sideways = false; }
    });

    go(0, false);
  }

  /* ------------------------------------------------------------ hearts */
  function paintHearts(root) {
    var wl = window.wishlist;
    if (!wl || typeof wl.isSaved !== 'function') return;
    root.querySelectorAll('[data-wishlist-save][data-product-id]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', wl.isSaved(btn.getAttribute('data-product-id')) ? 'true' : 'false');
    });
  }

  /* ------------------------------------------------------ photo shimmer */
  /* Until a card's photo has arrived its frame shimmers (np-collection.css
     LOADING), on first load, after a filter and on View all alike. */
  function shimmerUntilLoaded(card) {
    var wrapper = card.querySelector('.np-product-image-wrapper');
    var img = wrapper && wrapper.querySelector(':scope > img');
    if (!img || img.complete) return;
    wrapper.classList.add('is-loading');
    var done = function () { wrapper.classList.remove('is-loading'); };
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  }

  /* -------------------------------------------------------------- init */
  function init(root) {
    root = root || document;
    var cards = root.querySelectorAll('[data-np-card]');
    cards.forEach(shimmerUntilLoaded);
    if (PHONE.matches) cards.forEach(carousel);
    paintHearts(root);
  }

  window.npInitCards = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
  document.addEventListener('np:browse:updated', function (e) {
    init(e.detail && e.detail.root);
  });
  /* A phone turned into a tablet keeps its carousels; one that was a
     tablet gets them the first time it is narrow enough. */
  if (PHONE.addEventListener) {
    PHONE.addEventListener('change', function () { if (PHONE.matches) init(); });
  }
})();
